import { CompactInput, PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import React from 'react';
import styled from 'styled-components';
import { useIsSceneLook } from './propertiesPanelLook';
import { panel } from './panelTheme';
import { PropertiesPanelNumberField } from './propertiesPanelNumberField';
import { PropertiesPanelAxisLetter, PropertiesPanelAxisToggle } from './propertiesPanelAxisTogglesProperty';

const RangeInput = styled.input`
    flex: 1;
    min-width: 0;
`;

const NumberInput = styled(CompactInput)`
    width: 90px;
    flex-shrink: 0;
    flex: unset;
    text-align: center;
`;

// LO SLIDER DELLA SCENA: la parte già percorsa è colorata, e il pomello è un anello.
//
// `0 1` E NON `1 1`: non cresce mai — crescendo tornerebbero slider di lunghezza diversa da una
// riga all'altra, che è il difetto d'origine — e si stringe solo quando serve. Il pavimento è
// 44px, sotto il quale uno slider non è più trascinabile. L'ordine di cedimento è dichiarato:
// prima cede lo slider, il numero mai.
const SceneRange = styled.input<{ $pct: number }>`
    appearance: none;
    -webkit-appearance: none;
    flex: 0 1 140px;
    width: 140px;
    min-width: 44px;
    height: 16px;
    background: transparent;
    cursor: pointer;

    &::-webkit-slider-runnable-track {
        height: 5px;
        border-radius: 999px;
        background:
            linear-gradient(${panel.accent}, ${panel.accent}) 0 / ${(props) => props.$pct}% 100% no-repeat,
            ${panel.line};
    }

    &::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        width: 14px;
        height: 14px;
        margin-top: -4.5px;
        border-radius: 50%;
        background: ${panel.surface};
        box-shadow:
            inset 0 0 0 4px ${panel.accent},
            0 1px 3px rgba(30, 42, 56, 0.28);
    }

    &::-moz-range-track {
        height: 5px;
        border-radius: 999px;
        background: ${panel.line};
    }

    &::-moz-range-progress {
        height: 5px;
        border-radius: 999px;
        background: ${panel.accent};
    }

    &::-moz-range-thumb {
        width: 14px;
        height: 14px;
        border: 0;
        border-radius: 50%;
        background: ${panel.surface};
        box-shadow:
            inset 0 0 0 4px ${panel.accent},
            0 1px 3px rgba(30, 42, 56, 0.28);
    }

    &:focus-visible {
        outline: none;
    }

    &[disabled] {
        cursor: not-allowed;
        filter: grayscale(1);

        &::-webkit-slider-thumb {
            box-shadow: inset 0 0 0 4px ${panel.line};
        }
    }
`;

const SceneControl = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    min-width: 0;
`;

// LA LETTERA STA IN TESTA AL CONTROLLO, non accanto al numero: col margine che si prende, le lettere
// di righe diverse cadono alla stessa x — subito dopo il nome — e cursore e numero restano a destra,
// incolonnati con le righe che una lettera non ce l'hanno.
//
// SPENTA DA SOLA, SI SPEGNE LEI: la riga resta viva — una ruvidità vale anche senza la mappa da cui
// leggerla — e il velo tocca solo la lettera. Quando è spenta la riga intera, il velo ce l'ha già la
// riga, e un secondo lo moltiplicherebbe.
const Tag = styled.span<{ $off: boolean }>`
    display: inline-flex;
    flex: none;
    margin-right: auto;
    opacity: ${(props) => (props.$off ? 0.5 : 1)};
`;

export type PropertiesPanelRangePropertyProps = {
    id: string;
    label: React.ReactNode;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    /** Il suffisso dentro il campo: «°». Solo nel vestito della scena. */
    unit?: string;
    /**
     * IL CURSORE SI FERMA PRIMA DEL CAMPO. Alcune misure hanno un intervallo utile stretto e
     * nessun tetto vero — lo spessore di un materiale si regola quasi sempre fra 0 e 10, ma
     * ogni tanto serve 40. Con un solo `max` si sceglie fra un cursore inservibile, perché
     * copre un intervallo che non si usa mai, e un valore che non si può scrivere.
     *
     * Quando c'è, il cursore arriva fin qui e il campo numerico resta senza tetto.
     */
    sliderMax?: number;
    /**
     * LA LETTERA DEL CANALE DA CUI IL VALORE LEGGE — la R, la G o la B di una mappa ORM: il patto del
     * pacchetto detto riga per riga, invece che nel solo nome del canale. Solo nel vestito della scena.
     */
    tag?: PropertiesPanelAxisToggle;
    /** L'evento c'è solo quando il valore arriva da un campo: dallo stepper non c'è nessun evento da passare. */
    onChange?: (newValue: number, e?: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
};

export const PropertiesPanelRangeProperty: React.FC<PropertiesPanelRangePropertyProps> = ({
    id,
    label,
    value,
    min,
    max,
    step,
    unit,
    sliderMax,
    tag,
    onChange,
    disabled
}) => {
    const scene = useIsSceneLook();
    const top = sliderMax ?? max;

    if (scene) {
        // Il riempimento non lo sa il browser: `::-webkit-slider-runnable-track` non conosce il
        // valore, quindi la percentuale gliela si passa.
        const low = min ?? 0;
        const high = top ?? 100;
        const pct = high === low ? 0 : Math.min(100, Math.max(0, ((value - low) / (high - low)) * 100));

        return (
            <PropertiesPanelProperty disabled={disabled}>
                <PropertiesPanelLabel id={`${id}-label`} htmlFor={id}>
                    {label}
                </PropertiesPanelLabel>
                <SceneControl>
                    {tag && (
                        <Tag $off={Boolean(tag.disabled) && !disabled}>
                            <PropertiesPanelAxisLetter toggle={tag} />
                        </Tag>
                    )}
                    <SceneRange
                        id={`${id}-range`}
                        type='range'
                        min={min}
                        max={top}
                        step={step}
                        value={value}
                        disabled={disabled}
                        $pct={pct}
                        aria-labelledby={`${id}-label`}
                        onChange={(e) => {
                            if (!isNaN(e.target.valueAsNumber)) onChange?.(e.target.valueAsNumber, e);
                        }}
                    />
                    <PropertiesPanelNumberField
                        id={id}
                        value={value}
                        min={min}
                        max={sliderMax === undefined ? max : undefined}
                        step={step}
                        unit={unit}
                        disabled={disabled}
                        onChange={(next) => onChange?.(next)}
                    />
                </SceneControl>
            </PropertiesPanelProperty>
        );
    }

    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                <RangeInput
                    id={`${id}-range`}
                    type='range'
                    min={min}
                    max={top}
                    step={step}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!isNaN(e.target.valueAsNumber)) {
                            onChange?.(e.target.valueAsNumber, e);
                        }
                    }}
                />
                <NumberInput
                    id={id}
                    type={'number'}
                    min={min}
                    max={sliderMax === undefined ? max : undefined}
                    step={step}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!isNaN(e.target.valueAsNumber)) {
                            onChange?.(e.target.valueAsNumber, e);
                        }
                    }}
                />
            </PropertiesPanelControl>
        </PropertiesPanelProperty>
    );
};
